// src/documents/documentFields.js
//
// Campos que se pueden completar en el resumen previo a generar la LOR o la
// SPOL (DocumentPreflight). La clave es la misma de `missing` en
// lorData/spolData; cada campo dice dónde se guarda: en el reclamo
// (claimRecords) o en los montos del estimado (stepData `rs_pol`).
// Lo que se escribe aquí queda guardado (decisión del propietario,
// 2026-10-14): la próxima vez el documento ya sale completo.

const l = (es, en) => [es, en];

const CLAIM_TYPES = [
  { value: 'emergency', label: l('Emergencia', 'Emergency') },
  { value: 'non_emergency', label: l('No emergencia', 'Non-emergency') },
  { value: 'supplemental', label: l('Suplementario', 'Supplemental') }
];
const DEDUCTIBLE_TYPES = [
  { value: 'fixed', label: l('Monto fijo ($)', 'Fixed amount ($)') },
  { value: 'percentage', label: l('Porcentaje de la Cobertura A (%)', 'Percent of Coverage A (%)') }
];

export const DOCUMENT_FIELDS = {
  clientName: [{ name: 'clientName' }],
  contact: [
    { name: 'clientPhone', type: 'tel', label: l('Teléfono del cliente', 'Client phone') },
    { name: 'clientEmail', type: 'email', label: l('Correo del cliente', 'Client email') }
  ],
  insuranceCompanyName: [{ name: 'insuranceCompanyName' }],
  policyNumber: [{ name: 'policyNumber' }],
  claimNumber: [{ name: 'claimNumber' }],
  incidentDate: [{ name: 'incidentDate', type: 'date' }],
  lossDescription: [{ name: 'lossDescription', type: 'textarea' }],
  claimType: [{ name: 'claimType', type: 'select', options: CLAIM_TYPES }],
  agreedFeePercent: [{ name: 'agreedFeePercent', type: 'number' }],
  coverages: ['A', 'B', 'C', 'D'].map(code => ({ name: `coverage${code}`, type: 'number', label: l(`Cobertura ${code} ($)`, `Coverage ${code} ($)`) })),
  policyEffectiveDate: [{ name: 'policyEffectiveDate', type: 'date' }],
  policyExpirationDate: [{ name: 'policyExpirationDate', type: 'date' }],
  agentAgency: [{ name: 'agentAgency' }],
  agentAddress: [{ name: 'agentAddress' }],
  cause: [{ name: 'cause' }],
  lossTime: [{ name: 'lossTime', type: 'time' }],
  occupancy: [{ name: 'occupancy' }],
  insuredInterest: [{ name: 'insuredInterest', type: 'choice', options: ['Owner', 'Tenant'], label: l('Interés del asegurado (Owner / Tenant)', 'Insured interest (Owner / Tenant)') }],
  deductible: [
    { name: 'deductibleAmount', type: 'number', label: l('Deducible', 'Deductible') },
    { name: 'deductibleType', type: 'select', options: DEDUCTIBLE_TYPES, label: l('Tipo de deducible', 'Deductible type') }
  ],
  actualCashValue: [{ name: 'actualCashValue', type: 'number', target: 'estimate' }],
  claimed: [{ name: 'rcv', type: 'number', target: 'estimate', label: l('RCV (del estimado)', 'RCV (from estimate)') }],
  mortgage: [
    { name: 'mortgageCompanyName', label: l('Hipotecaria (mortgage)', 'Mortgage company') },
    { name: 'mortgageLoanNumber', label: l('Número de préstamo', 'Loan number') }
  ],
  emsVendors: [{ name: 'emsVendors', type: 'number', target: 'estimate', label: l('EMS / Vendors (total)', 'EMS / Vendors (total)') }]
};

const asInput = value => (value === null || value === undefined ? '' : String(value));

// Valores iniciales del formulario: lo que ya tienen el reclamo y el estimado.
export const initialDocumentForm = (keys, claimRecord = {}, estimate = {}) => {
  const form = {};
  keys.forEach(key => (DOCUMENT_FIELDS[key] || []).forEach(field => {
    form[field.name] = asInput((field.target === 'estimate' ? estimate : claimRecord)[field.name]);
  }));
  return form;
};

// Solo lo que cambió, separado por destino. Números como número; un campo
// que se vacía no se guarda (el resumen completa datos, no los borra).
export const documentFormChanges = (keys, form, claimRecord = {}, estimate = {}) => {
  const initial = initialDocumentForm(keys, claimRecord, estimate);
  const changes = { claim: {}, estimate: {} };
  keys.forEach(key => (DOCUMENT_FIELDS[key] || []).forEach(field => {
    const raw = String(form[field.name] ?? '').trim();
    if (raw === '' || raw === initial[field.name].trim()) return;
    let value = raw;
    if (field.type === 'number') {
      value = Number(raw.replace(/[$,\s%]/g, ''));
      if (!Number.isFinite(value)) return;
    }
    changes[field.target === 'estimate' ? 'estimate' : 'claim'][field.name] = value;
  }));
  return changes;
};

export const isEditableDocumentKey = key => Boolean(DOCUMENT_FIELDS[key]);
